import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownUp, Search, Pencil, Check } from 'lucide-react';
import AddIcon from '@mui/icons-material/Add';
import {
  Tooltip,
  List,
  ListItem,
  TextField,
  IconButton,
  Button,
  Box,
  InputAdornment,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {
  fetchConversations,
  fetchConversationDetails,
  updateConversationStatus,
  updateConversationShortNote,
  deleteConversation,
  fetchPromptTemplates,
} from '../../utils/axiosApiRequest';
// import '../../App.css';
import { ConfirmDeleteDialog } from './confirm-delete-dialog';
import { TranscriberErrorDialog } from './transcriber-error-dialog';
import { NewConversationErrorDialog } from './new-conversation-error-dialog';

const Conversations = ({
  setShowControls,
  setSelectedConversation,
  selectedConversation,
  setMessages,
  setSummary,
  setSummaryId,
  setAudioFilename,
  recording,
  startNewConversation,
  setPromptTemplates,
  setSelectedPrompt,
  setLanguage,
  setAudioReady,
  showTranscriberErrorMessage,
  setShowTranscriberErrorMessage,
  showNewConversationErrorMessage,
  setShowNewConversationErrorMessage,
  triggerUpdateConversationId,
  isMobile = false,
}) => {
  const { t, i18n } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortAscending, setSortAscending] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState({
    display: false,
    id: null,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    const loadConversations = async () => {
      setLoading(true);
      try {
        const templates = await fetchPromptTemplates();
        const sortedTemplates = templates.sort((a, b) => a.id - b.id);
        if (sortedTemplates.length > 0) {
          setPromptTemplates(sortedTemplates);
        }
        const data = await fetchConversations();
        const sortedConversations = data.sort((a, b) => b.id - a.id);
        setConversations(sortedConversations);

        if (!isMobile && sortedConversations.length > 0) {
          let idToSelect = 0;
          if (selectedConversation && selectedConversation.id > 0) {
            const idx = sortedConversations.findIndex(item => item.id === selectedConversation.id);
            if (idx !== -1) 
              idToSelect = idx;
          }
          setSelectedConversation(sortedConversations[idToSelect]);
          loadConversationDetails(sortedConversations[idToSelect]);
        }

        // Only auto-select the first conversation in desktop mode
        // if (sortedConversations.length > 0 && !isMobile) {
        //   setSelectedConversation(sortedConversations[0]);
        //   loadConversationDetails(sortedConversations[0]);
        // }
      } catch (error) {
        console.error('Error fetching conversations:', error);
      } finally {
        setLoading(false);
      }
    };
    loadConversations();
  }, []);

  useEffect(() => {
    if (triggerUpdateConversationId > -1) {
      // console.log("trigger update on: ", triggerUpdateConversationId);
      const idToSelect = conversations.findIndex(item => item.id === triggerUpdateConversationId);
      if (idToSelect !== -1) {
        fetchConversationDetails(triggerUpdateConversationId).then(data => {
          const newestSummary = data.summaries.reduce((latest, current) => {
            return current.unix_timestamp > (latest?.unix_timestamp || 0)
              ? current
              : latest;
          }, null);
          if (newestSummary) {
            const short_summary = newestSummary.summary.split('\n').slice(0, 3).join('\n');
            if (short_summary && short_summary.length > 0) {
              setConversations(prev =>
                prev.map(con =>
                  con.id === data.id ? { ...con, short_summary: short_summary } : con
                )
              );
            }
          }
        });
      }
    }
  }, [triggerUpdateConversationId]);

  const loadConversationDetails = async (conversation) => {
    try {
      setSelectedConversation(conversation);
      setAudioFilename(null);
      setAudioReady(false);

      const data = await fetchConversationDetails(conversation.id);

      if (data.transcript.length === 0 && data.summaries.length === 0) {
        setShowControls(true);
        setMessages([]);
        setSummary(null);
        setSummaryId(null);
        setAudioFilename(null);
        setSelectedPrompt('');
        return;
      }

      // Set messages, summary, and other details
      setMessages(data.transcript.map((msg) => msg.message));
      const newestSummary = data.summaries.reduce((latest, current) => {
        return current.unix_timestamp > (latest?.unix_timestamp || 0)
          ? current
          : latest;
      }, null);

      if (newestSummary) {
        setSummary(newestSummary.summary);
        setSummaryId(newestSummary.id); // Store the id of the newest summary for updates

        if (newestSummary.prompt_template) {
          setSelectedPrompt(newestSummary.prompt_template.id);
        } else {
          setSelectedPrompt('');
        }
      } else {
        setSummary(''); // Fallback if there are no summaries
      }

      setAudioFilename(data.audio_filename);
      setAudioReady(true);
      setLanguage(data.language);

      // Only in desktop mode, hide controls
      if (!isMobile && !recording) {
        setShowControls(false);
      }

      if (data.short_summary && data.short_summary.length > 0) {
        setConversations(prev =>
          prev.map(con =>
            con.id === data.id ? { ...con, short_summary: data.short_summary } : con
          )
        );
      }
    } catch (error) {
      setMessages([]);
      setSummary(null);
      setAudioFilename(null);
      setAudioReady(false);
      setShowControls(true);
      console.error('Error fetching conversation details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = () => {
    const sorted = [...conversations].sort((a, b) => {
      return sortAscending
        ? a.start_unix_timestamp - b.start_unix_timestamp
        : b.start_unix_timestamp - a.start_unix_timestamp;
    });
    setSortAscending(!sortAscending);
    setConversations(sorted);
  };

  const handleNewConversation = async () => {
    if (isMobile) {
      // In mobile mode, let the parent handle the new conversation and navigation
      startNewConversation();
      return;
    }

    // Original desktop behavior
    try {
      const data = await startNewConversation();
      const newConversation = {
        id: data.id,
        start_unix_timestamp: Math.floor(Date.now() / 1000),
        end_unix_timestamp: 0,
        short_note: data.short_note,
        status: data.status,
      };
      setConversations([newConversation, ...conversations]);
      setSelectedConversation(newConversation);
      setShowControls(true);
      setMessages([]);
      setSummary(null);
      setSummaryId(null);
      setAudioFilename(data.audio_filename);
    } catch (error) {
      console.error('Failed to create a new conversation:', error);
    }
  };

  const updateStatus = async (conversationId, newStatus) => {
    try {
      await updateConversationStatus(conversationId, { status: newStatus });
      
      // Update the conversation in the list
      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          conv.id === conversationId ? { ...conv, status: newStatus } : conv
        )
      );
      
      // If this is the currently selected conversation, update it as well
      if (selectedConversation && selectedConversation.id === conversationId) {
        setSelectedConversation(prev => ({
          ...prev,
          status: newStatus
        }));
      }
    } catch (error) {
      console.error('Error updating conversation status:', error);
    }
  };

  const updateShortNote = async (conversationId, newShortNote) => {
    try {
      await updateConversationShortNote(conversationId, {
        short_note: newShortNote,
      });
      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          conv.id === conversationId
            ? { ...conv, short_note: newShortNote }
            : conv
        )
      );
    } catch (error) {
      console.error('Error updating conversation short_note:', error);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    const response = await deleteConversation(conversationId);
    if (!response.success) {
      alert('failed to delete conversation');
      return false;
    }

    // Find the index of the conversation being deleted
    const deletedIndex = conversations.findIndex(
      (c) => c.id === conversationId
    );

    // Filter out the deleted conversation
    const updatedConversations = conversations.filter(
      (c) => c.id !== conversationId
    );
    setConversations(updatedConversations);

    if (selectedConversation?.id === conversationId) {
      // Handle differently based on mobile or desktop mode
      if (isMobile) {
        // In mobile mode, just clear the selection and stay on the conversations page
        // Use null instead of an empty object to prevent any redirection
        setSelectedConversation(null);
        setShowControls(false);
        setMessages([]);
        setSummary(null);
        setSummaryId(null);
        setAudioFilename(null);
        setAudioReady(false);
        return true; // Return true to indicate successful deletion without navigation
      } else {
        // In desktop mode, select the previous conversation in the list
        if (updatedConversations.length > 0) {
          // If the deleted conversation was the first one, select the new first one
          // Otherwise, select the conversation that was before the deleted one
          const newIndex = deletedIndex === 0 ? 0 : deletedIndex - 1;
          const newSelectedConversation = updatedConversations[newIndex];

          // Load the details of the newly selected conversation
          setSelectedConversation(newSelectedConversation);
          loadConversationDetails(newSelectedConversation);
          setShowControls(false);
        } else {
          // If no conversations left, clear everything
          setShowControls(false);
          setMessages([]);
          setSummary(null);
          setSummaryId(null);
          setSelectedConversation(null);
          setAudioFilename(null);
          setAudioReady(false);
        }
      }
    }
    return true;
  };

  // Filter conversations based on search term
  const filteredConversations = conversations.filter((conv) =>
    conv.short_note.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (unixTimestamp) => {
    const date = new Date(unixTimestamp * 1000);
    const now = new Date();

    // Check if the date is today
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (isToday) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${t('common.today')}, ${hours}:${minutes}`;
    } else {
      const options = {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      };
      return new Intl.DateTimeFormat(i18n.language, options).format(date);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
        }}
      >
        {/* Title and New Conversation Button */}
        {!isMobile && (
          <Box
            sx={{
              height: '68px',
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #e0e0e0',
              px: 2,
            }}
          >
            <Typography variant='h2' component='h2' sx={{}}>
              {t('conversations.title')}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            px: 2,
            py: 1.5,
          }}
        >
          <Button
            variant='buttonHighlight'
            startIcon={<AddIcon />}
            onClick={handleNewConversation}
            disabled={recording}
            size='small'
            fullWidth
          >
            {t('conversations.newConversation')}
          </Button>

          {/* Search field */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TextField
              fullWidth
              variant='outlined'
              placeholder={t('conversations.searchPlaceholder')}
              size='small'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: 'secondary.main',
                  },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '14px',
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search strokeWidth={1.25} size={18} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Tooltip
              title={
                sortAscending
                  ? t('conversations.sortDescending')
                  : t('conversations.sortAscending')
              }
            >
              <IconButton onClick={handleSort} sx={{ ml: 1 }}>
                <ArrowDownUp strokeWidth={1.25} size={20} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          height: 0,
          '&::-webkit-scrollbar': {
            display: { xs: 'none', md: 'block' },
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress color={theme.palette.secondary.main} />
          </Box>
        ) : (
          <List sx={{ pl: 2, pr: { xs: 2, md: 1, xl: 2 } }}>
            {filteredConversations.map((conv) => (
              <ListItem
                key={conv.id}
                onClick={() => {
                  if (!recording) {
                    loadConversationDetails(conv);
                  }
                }}
                sx={{
                  cursor: recording ? 'not-allowed' : 'pointer',
                  bgcolor:
                    selectedConversation?.id === conv.id
                      ? 'secondary.light'
                      : 'background.paper',
                  '&:hover': {
                    bgcolor: recording
                      ? selectedConversation?.id === conv.id
                        ? 'secondary.light'
                        : 'background.paper'
                      : 'secondary.light',
                  },
                  py: 1.5,
                  px: 1.5,
                  borderRadius: '12px',
                  border:
                    selectedConversation?.id === conv.id
                      ? `2px solid ${theme.palette.secondary.main}`
                      : '1px solid #E4E4E7',
                  mb: 1,
                  opacity:
                    recording && selectedConversation?.id !== conv.id ? 0.6 : 1,
                  transition: 'opacity 0.2s ease',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: '8px',
                  }}
                >
                  {/* Top row with title, data and status */}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexGrow: 1,
                          gap: '8px',
                        }}
                      >
                        <TextField
                          value={conv.short_note}
                          variant='standard'
                          placeholder={
                            editingNoteId === conv.id ? conv.short_note : ''
                          }
                          InputProps={{
                            disableUnderline: true,
                            sx: {
                              fontSize: '16px',
                              fontWeight: 600,
                              cursor:
                                editingNoteId === conv.id ? 'text' : 'pointer',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                              '& .MuiInputBase-input': {
                                cursor:
                                  editingNoteId === conv.id
                                    ? 'text'
                                    : 'pointer',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                              ...(editingNoteId === conv.id && {
                                backgroundColor: '#ffff',
                                padding: '0px 8px',
                                border: `1px solid ${theme.palette.secondary.main}`,
                                borderRadius: '4px',
                                marginBottom: '4px',
                              }),
                            },
                            readOnly: editingNoteId !== conv.id,
                          }}
                          fullWidth
                          onClick={(e) => {
                            if (editingNoteId === conv.id) {
                              e.stopPropagation();
                            }
                          }}
                          data-conversation-id={conv.id}
                          onChange={(e) => {
                            const updatedNote = e.target.value;
                            const updatedConversations = conversations.map(
                              (c) =>
                                c.id === conv.id
                                  ? { ...c, short_note: updatedNote }
                                  : c
                            );
                            setConversations(updatedConversations);
                          }}
                          onBlur={(e) => {
                            if (editingNoteId === conv.id) {
                              const updatedNote = e.target.value;
                              updateShortNote(conv.id, updatedNote);
                              setEditingNoteId(null);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.key === 'Enter' &&
                              editingNoteId === conv.id
                            ) {
                              e.preventDefault();
                              const updatedNote = e.target.value;
                              updateShortNote(conv.id, updatedNote);
                              setEditingNoteId(null);
                              e.target.blur();
                            }
                          }}
                        />
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (editingNoteId === conv.id) {
                              const updatedNote = conversations.find(
                                (c) => c.id === conv.id
                              )?.short_note;
                              if (updatedNote) {
                                updateShortNote(conv.id, updatedNote);
                              }
                              setEditingNoteId(null);
                            } else {
                              setEditingNoteId(conv.id);
                              setTimeout(() => {
                                const textField = document.querySelector(
                                  `[data-conversation-id="${conv.id}"]`
                                );
                                if (textField) textField.focus();
                              }, 50);
                            }
                          }}
                          disabled={recording}
                        >
                          {editingNoteId === conv.id ? (
                            <Check size={20} strokeWidth={1.25} />
                          ) : (
                            <Pencil size={18} strokeWidth={1.25} />
                          )}
                        </IconButton>
                      </Box>
                      {/* Date */}
                      <Typography
                        variant='body2'
                        sx={{
                          fontSize: '12px',
                          fontWeight: 400,
                          color: '#09090B',
                          textAlign: 'right',
                          minWidth: 'fit-content',
                        }}
                      >
                        {formatDate(conv.start_unix_timestamp)}
                      </Typography>
                    </Box>
                    {/* Status row */}
                    <Typography
                      variant='body2'
                      sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: theme.palette.primary.light,
                      }}
                    >
                      {t('common.status')}:{' '}
                      <span
                        style={{
                          color:
                            conv.status === 'APPROVED'
                              ? theme.palette.secondary.main
                              : 'inherit',
                        }}
                      >
                        {t(`conversations.${conv.status.toLowerCase()}`)}
                      </span>
                    </Typography>
                  </Box>

                  {/* Summary */}
                  <Typography
                    variant='body2'
                    sx={{
                      mt: 0.5,
                      fontStyle: conv.short_summary ? 'normal' : 'italic',
                      fontSize: '14px',
                      lineHeight: 1.43,
                      color: theme.palette.primary.light,
                      fontWeight: 400,
                      maxHeight: '48px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {conv.short_summary
                      ? conv.short_summary
                      : t('conversations.noSummaryAvailable')}
                  </Typography>

                  {/* Buttons row */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '8px',
                      mt: 0.5,
                    }}
                  >
                    <Button
                      variant='badgeSecondary'
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteDialog({ display: true, id: conv.id });
                      }}
                      disabled={recording}
                    >
                      {t('common.delete')}
                    </Button>

                    <Button
                      variant={
                        conv.status === 'DRAFT'
                          ? 'badgePrimary'
                          : 'badgeSecondary'
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        if (conv.status === 'DRAFT') {
                          updateStatus(conv.id, 'APPROVED');
                        } else if (conv.status === 'APPROVED') {
                          updateStatus(conv.id, 'DRAFT');
                        }
                      }}
                      disabled={recording}
                    >
                      {conv.status === 'DRAFT'
                        ? t('conversations.approve')
                        : t('conversations.revise')}
                    </Button>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <ConfirmDeleteDialog
        open={showDeleteDialog.display}
        onClose={() => setShowDeleteDialog({ display: false, id: null })}
        onDelete={() => {
          const id = showDeleteDialog.id;
          setShowDeleteDialog({ display: false, id: null });
          handleDeleteConversation(id);
        }}
      />

      <TranscriberErrorDialog
        open={showTranscriberErrorMessage}
        onClose={() => setShowTranscriberErrorMessage(false)}
      />

      <NewConversationErrorDialog
        open={showNewConversationErrorMessage}
        onClose={() => setShowNewConversationErrorMessage(false)}
      />
    </Box>
  );
};

export default Conversations;
